import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination
} from '../../styles/StyledComponents';
import { 
  FaPlus, 
  FaSearch, 
  FaFileExcel, 
  FaFilePdf, 
  FaEye, 
  FaEdit, 
  FaFilter, 
  FaSortAmountDown, 
  FaSortAmountUp, 
  FaDownload,
  FaMoneyBillWave,
  FaBuilding,
  FaTools,
  FaTruck,
  FaHandshake,
  FaCog,
  FaCalendarAlt,
  FaUser,
  FaReceipt
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const PageContainer = styled.div`
  margin-bottom: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ProjectInfoCard = styled(Card)`
  margin-bottom: 2rem;
  border: 2px solid #F68B21;
  background: linear-gradient(135deg, #fff9f0 0%, #ffffff 100%);
  
  .project-header {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    color: white;
    padding: 1rem;
    border-radius: 8px 8px 0 0;
    
    h3 {
      margin: 0;
      font-size: 1.2rem;
      font-weight: 600;
    }
    
    .project-meta {
      margin-top: 0.5rem;
      font-size: 0.85rem;
      opacity: 0.9;
    }
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  
  label {
    font-size: 0.9rem;
    font-weight: 700;
    color: #141340;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const TableCard = styled(Card)`
  margin-bottom: 2rem;
`;

const TableActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ResultCount = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TableActionButton = styled(OutlineButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const StatusBadge = styled(Badge)`
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
  
  .empty-icon {
    font-size: 3rem;
    color: #dee2e6;
    margin-bottom: 1rem;
  }
  
  .empty-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
  }
  
  .empty-description {
    font-size: 0.9rem;
    color: #6c757d;
  }
`;

const CashierExpensesList: React.FC = () => {
  const { user } = useAuth();
  const [assignedProject, setAssignedProject] = useState<any>(null);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    const loadExpensesData = async () => {
      try {
        setLoading(true);
        
        // Get projects assigned to the current cashier
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        if (assignedProjects.length > 0) {
          // For now, use the first assigned project
          // In the future, you might want to show multiple projects or let user select
          const project = assignedProjects[0];
          setAssignedProject(project);
          
          // Load expenses for the assigned project
          const expensesResponse = await apiService.getExpenses({ 
            projectId: project._id || project.id 
          });
          setExpenses(expensesResponse.data || []);
        } else {
          // No projects assigned to this cashier
          setAssignedProject(null);
          setExpenses([]);
        }
      } catch (error) {
        console.error('Error loading expenses data:', error);
        // Fallback to empty data
        setAssignedProject(null);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      loadExpensesData();
    }
  }, [user]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!assignedProject) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>
            <div className="title-icon">
              <IconWrapper icon={FaReceipt} />
            </div>
            Project Expenses
          </PageTitle>
        </PageHeader>
        
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '3rem' }}>
            <p>No project assigned. Please contact your supervisor to get assigned to a project.</p>
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'danger';
      default: return 'info';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Materials': return FaBuilding;
      case 'Labor': return FaTools;
      case 'Equipment': return FaTruck;
      case 'Sub-Contracts': return FaHandshake;
      case 'Others': return FaCog;
      default: return FaMoneyBillWave;
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.description.toLowerCase().includes(filters.search.toLowerCase()) ||
                         expense.supplier.toLowerCase().includes(filters.search.toLowerCase()) ||
                         expense.receiptNumber.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesCategory = !filters.category || expense.category === filters.category;
    const matchesStatus = !filters.status || expense.status === filters.status;
    
    const matchesDateFrom = !filters.dateFrom || new Date(expense.date) >= new Date(filters.dateFrom);
    const matchesDateTo = !filters.dateTo || new Date(expense.date) <= new Date(filters.dateTo);
    
    return matchesSearch && matchesCategory && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentExpenses = filteredExpenses.slice(startIndex, endIndex);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <div className="title-icon">
            <IconWrapper icon={FaReceipt} />
          </div>
          Project Expenses
        </PageTitle>
        <FlexContainer gap="1rem">
          <ActionButton as={Link} to="/cashier/expense/new">
            <IconWrapper icon={FaPlus} />
            Record Expense
          </ActionButton>
          <OutlineButton>
            <IconWrapper icon={FaFileExcel} />
            Export Excel
          </OutlineButton>
        </FlexContainer>
      </PageHeader>

      {assignedProject ? (
        <ProjectInfoCard>
          <div className="project-header">
            <h3>{assignedProject.name}</h3>
            <div className="project-meta">
              Project ID: {assignedProject._id || assignedProject.id} • Total Expenses: {formatCurrency(totalExpenses)}
            </div>
          </div>
          <CardBody>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>Location:</strong> {assignedProject.location}
              </div>
              <div>
                <strong>Project Manager:</strong> {
                  assignedProject.assignedStaff?.find((staff: any) => staff.role === 'Project Manager')?.name || 'Not assigned'
                }
              </div>
            </div>
          </CardBody>
        </ProjectInfoCard>
      ) : (
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '3rem' }}>
            <p>No project assigned. Please contact your supervisor to get assigned to a project.</p>
          </CardBody>
        </Card>
      )}

      <FiltersContainer>
        <FilterGroup>
          <label>Search</label>
          <Input
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search expenses..."
          />
        </FilterGroup>
        
        <FilterGroup>
          <label>Category</label>
          <Select
            name="category"
            value={filters.category}
            onChange={handleFilterChange}
          >
            <option value="">All Categories</option>
            <option value="Materials">Materials</option>
            <option value="Labor">Labor</option>
            <option value="Equipment">Equipment</option>
            <option value="Sub-Contracts">Sub-Contracts</option>
            <option value="Others">Others</option>
          </Select>
        </FilterGroup>
        
        <FilterGroup>
          <label>Status</label>
          <Select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">All Status</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
          </Select>
        </FilterGroup>
        
        <FilterGroup>
          <label>Date From</label>
          <Input
            name="dateFrom"
            type="date"
            value={filters.dateFrom}
            onChange={handleFilterChange}
          />
        </FilterGroup>
        
        <FilterGroup>
          <label>Date To</label>
          <Input
            name="dateTo"
            type="date"
            value={filters.dateTo}
            onChange={handleFilterChange}
          />
        </FilterGroup>
      </FiltersContainer>

      <TableCard>
        <CardBody>
          <TableActionsRow>
            <ResultCount>
              <IconWrapper icon={FaReceipt} />
              {filteredExpenses.length} expenses found
            </ResultCount>
            <FlexContainer gap="0.5rem">
              <OutlineButton>
                <IconWrapper icon={FaDownload} />
                Download
              </OutlineButton>
            </FlexContainer>
          </TableActionsRow>
          
          <TableContainer>
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>Description</TableHeader>
                  <TableHeader>Category</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Date</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Recorded By</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {currentExpenses.length > 0 ? (
                  currentExpenses.map((expense) => (
                    <TableRow key={expense._id || expense.id}>
                      <TableCell>
                        <div style={{ fontWeight: '500', color: '#141340' }}>
                          {expense.description}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          {expense.supplier} • {expense.receiptNumber}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <IconWrapper icon={getCategoryIcon(expense.category)} />
                          {expense.category}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: '600', color: '#F68B21' }}>
                          {formatCurrency(expense.amount)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {new Date(expense.date).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={getStatusVariant(expense.status)}>
                          {expense.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {expense.recordedBy && typeof expense.recordedBy === 'object' ? expense.recordedBy.name : expense.recordedBy || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#adb5bd' }}>
                          {new Date(expense.recordedAt).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <Link to={`/cashier/expense/${expense._id || expense.id}`}>
                            <TableActionButton>
                              <IconWrapper icon={FaEye} />
                              View
                            </TableActionButton>
                          </Link>
                          <Link to={`/cashier/expense/edit/${expense._id || expense.id}`}>
                            <TableActionButton>
                              <IconWrapper icon={FaEdit} />
                              Edit
                            </TableActionButton>
                          </Link>
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState>
                        <div className="empty-icon">
                          <IconWrapper icon={FaReceipt} />
                        </div>
                        <div className="empty-title">No expenses found</div>
                        <div className="empty-description">
                          {filters.search || filters.category || filters.status || filters.dateFrom || filters.dateTo
                            ? 'Try adjusting your filters to see more results.'
                            : 'Start recording expenses for this project to track spending.'
                          }
                        </div>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableContainer>
          
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '0.5rem', 
              marginTop: '2rem' 
            }}>
              <OutlineButton 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </OutlineButton>
              <span style={{ 
                padding: '0.5rem 1rem', 
                color: '#6c757d',
                display: 'flex',
                alignItems: 'center'
              }}>
                Page {currentPage} of {totalPages}
              </span>
              <OutlineButton 
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </OutlineButton>
            </div>
          )}
        </CardBody>
      </TableCard>
      
      <div style={{ 
        textAlign: 'right', 
        padding: '2rem 1rem 1rem', 
        color: '#6c757d', 
        fontSize: '0.8rem' 
      }}>
        Developed by Sandfish
      </div>
    </PageContainer>
  );
};

export default CashierExpensesList; 